/* eslint-disable react/prop-types */

import React, { useRef, useState, useMemo } from 'react';
import Select from 'react-select';
import { useTheme } from '@mui/material/styles';
import NoSsr from '@mui/material/NoSsr';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CancelIcon from '@mui/icons-material/Cancel';
import { WarningRounded } from '@mui/icons-material';
import { buildInlineSelectStyles } from '#component/form/react-select-styles';
import propTypes from './prop-types';
import {
  DotIcon,
  DotsCntPopup,
  ErrorContainer,
  InputContainer,
  MessageTypography,
  PlaceholderTypography,
  RootContainer,
  SingleValueTypography,
  StyledChip,
  ValueContainerDiv
} from './style';
import { getProjectDateRange } from '#helper';

function renderDate(p, mb = 1, isDisabled) {
  return (
    <span
      style={{
        color: `${isDisabled ? 'inherit' : '#667'}`,
        fontSize: '12px',
        width: 170,
        marginLeft: 'auto'
      }}
    >
      {
        p.expirationDate && new Date(p.expirationDate) < new Date() ?
          <WarningRounded
            style={{
              width: 18,
              height: 18,
              color: '#FACE31',
              verticalAlign: 'bottom',
              marginRight: 5,
              marginBottom: mb
            }}
          /> :
          null
      }
      {getProjectDateRange(p)}
    </span>
  );
}

function NoOptionsMessage(props) {
  return (
    <MessageTypography color="textSecondary" {...props.innerProps}>
      {props.children}
    </MessageTypography>
  );
}

const inputComponent = React.forwardRef(
  (props, ref) => <InputContainer ref={ref} {...props} />
);

function Control(props) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          children: props.children,
          ...props.innerProps
        }
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  const { data: { disabled, disabledMsg } } = props;
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
        paddingLeft: 15,
        display: 'flex',
        flexWrap: 'wrap',
        whiteSpace: 'normal'
      }}
      disabled={Boolean(disabled)}
      {...props.innerProps}
    >
      <DotsCntPopup>
        {props.data.ancestors && props.data.ancestors.map((el, i) =>
          <DotIcon key={i} fontSize="inherit" />)}
      </DotsCntPopup>
      <span>
        {props.children}
      </span>
      {disabled ?
        <span
          style={{
            color: '#667',
            fontSize: '12px',
            marginLeft: 'auto',
            width: 170
          }}
        >
          {disabledMsg}
        </span> : props.selectProps.showDateRange ? renderDate(props.data) : null}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <PlaceholderTypography color="textSecondary" {...props.innerProps}>
      {props.children}
    </PlaceholderTypography>
  );
}

function SingleValue(props) {
  const { isDisabled } = props.selectProps;
  return (
    <SingleValueTypography
      {...props.innerProps}
      disabled={isDisabled}
      style={isDisabled ? { color: props.theme.colors.neutral30 } : {}}
    >
      {props.children}
      {props.selectProps.showDateRange ?
        <span
          style={{
            position: 'absolute',
            right: 75,
            marginLeft: 5,
            color: `${isDisabled ? props.theme.colors.neutral30 : 'inherit'}`
          }}
        >
          {renderDate(props.data, 3, isDisabled)}
        </span> : null}
    </SingleValueTypography>
  );
}

function ValueContainer(props) {
  return (
    <ValueContainerDiv>
      {props.children}
    </ValueContainerDiv>
  );
}

function MultiValue(props) {
  return (
    <StyledChip
      tabIndex={-1}
      label={props.children}
      isFocused={props.isFocused}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

const LoadingMessage = props => (
  <MessageTypography color="textSecondary" {...props.innerProps}>
    {props.selectProps.inputValue.length === 1 ? 'Enter 2 chars for searching' : 'Loading...'}
  </MessageTypography>
);

const defaultComponents = {
  Control,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  LoadingMessage
};

const IntegrationReactSelect = props => {
  const {
    label,
    data,
    placeholder,
    defaultData,
    meta = {},
    disabled,
    className = '',
    showDateRange = false,
    showProjectName,
    components: componentsOverride = {},
    forceError,
    fetchProjects,
    clearProjects,
    textFieldOnFocus,
    loading,
    setData
  } = props;

  const theme = useTheme();
  const selectRef = useRef(null);
  const [multi, setMulti] = useState(null);

  const components = { ...defaultComponents, ...componentsOverride };

  const handleChange = value => {
    setMulti(value);
    setData(value);

    if (selectRef.current?.inputRef) {
      selectRef.current.inputRef.blur();
    }

    if (clearProjects) {
      clearProjects();
    }
  };

  const selectStyles = useMemo(() => buildInlineSelectStyles({ theme }), [theme]);

  const isTouchedOrSubmitted = Boolean(
    meta.touched || meta.isTouched || meta.isSubmitted || meta.submitFailed || meta.isDirty
  );
  const errorObj = meta.error;
  const errorMessage = typeof errorObj === 'object' ? errorObj?.message : errorObj;
  const invalid = Boolean(
    (isTouchedOrSubmitted || forceError) && (meta.invalid || meta.error)
  );

  return (
    <RootContainer className={`${invalid ? 'invalid' : ''} ${className} ${disabled ? 'disabled' : ''}`}>
      <NoSsr>
        <Select
          styles={selectStyles}
          textFieldProps={{
            label,
            InputLabelProps: {
              shrink: true
            },
            onChange: fetchProjects || null,
            onBlur: clearProjects || null,
            onFocus: textFieldOnFocus || null
          }}
          placeholder={placeholder}
          options={loading ? [] : data}
          isLoading={loading}
          components={components}
          value={multi || defaultData}
          onChange={handleChange}
          menuPlacement="top"
          isDisabled={disabled}
          showDateRange={showDateRange}
          showProjectName={showProjectName}
          ref={selectRef}
          aria-label="warehouse-select"
        />
        {invalid && errorMessage &&
          <ErrorContainer>
            {errorMessage}
          </ErrorContainer>}
      </NoSsr>
    </RootContainer>
  );
};

IntegrationReactSelect.propTypes = propTypes;

export default IntegrationReactSelect;
